import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import {
  Warning as WarningIcon,
  ShoppingCart as ShoppingCartIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';

interface IntervalMismatchDialogProps {
  open: boolean;
  onClose: () => void;
  currentInterval: 'monthly' | 'yearly';
  attemptedInterval: 'monthly' | 'yearly';
  onClearCart: () => void;
  onKeepCurrent: () => void;
}

export const IntervalMismatchDialog: React.FC<IntervalMismatchDialogProps> = ({
  open,
  onClose,
  currentInterval,
  attemptedInterval,
  onClearCart,
  onKeepCurrent,
}) => {
  const handleClearCart = () => {
    onClearCart();
    onClose();
  };

  const handleKeepCurrent = () => {
    onKeepCurrent();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleKeepCurrent}
      maxWidth="sm"
      fullWidth
      aria-labelledby="interval-mismatch-dialog-title"
    >
      <DialogTitle id="interval-mismatch-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Billing Interval Mismatch</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Stripe requires all items in a single checkout to have the same billing interval.
        </Alert>
        
        <Typography variant="body1" paragraph>
          Your cart contains <strong>{currentInterval}</strong> subscriptions.
        </Typography>
        
        <Typography variant="body1" paragraph>
          You're trying to add a <strong>{attemptedInterval}</strong> subscription.
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          To proceed, you can either clear your current cart and add the new item, 
          or keep your current cart items.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleClearCart}
          color="error"
        >
          Clear cart and add new item
        </Button>
        <Button
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          onClick={handleKeepCurrent}
          autoFocus
        >
          Keep current cart
        </Button>
      </DialogActions>
    </Dialog>
  );
};